/* Mantleford's semantic events. Tag loading, pageviews and reporting belong to the adapter. */
(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) module.exports = factory;
  else if (!root.MantlefordEngagement) root.MantlefordEngagement = factory(root);
})(typeof window === 'undefined' ? null : window, function (win) {
  'use strict';
  const VERSION = '2026-09-10';
  const EVENTS = new Set(['outfit_view', 'build_look_view', 'compare_open', 'piece_change', 'affiliate_click', 'external_click']);
  const CTA = new Set(['selected-card', 'selected-piece', 'comparison', 'card-inline-offer', 'illustration-product-link', 'guide-source']);
  const doc = win.document;
  let instance;

  const slug = value => typeof value === 'string' && /^[a-z0-9][a-z0-9_-]{0,99}$/.test(value) ? value : '';
  const label = value => typeof value === 'string' ? value.replace(/[\u0000-\u001f\u007f]/g, '').slice(0,100) : '';
  const aesthetic = value => slug(label(value).toLowerCase().replace(/\s+/g, '-'));
  const affiliate = item => item.kind === 'affiliate' && item.commission === true;
  function url(value) { try { const u = new win.URL(value, win.location.href); return u.protocol === 'https:' ? u : null; } catch (_) { return null; } }

  function excluded() {
    try {
      if (!['mantleford.com', 'www.mantleford.com'].includes(win.location.hostname)) return true;
      if (win.self !== win.top || win.Shopify?.designMode === true) return true;
      if (win.Shopify?.theme?.role && win.Shopify.theme.role !== 'main') return true;
      const query = new win.URL(win.location.href).searchParams;
      if (['preview_theme_id', 'preview_script_id', 'theme_editor', 'preview_token'].some(k => query.has(k))) return true;
      if (query.get('mf_analytics') === 'off' || query.get('analytics_test') === '1') return true;
      if (doc.documentElement.hasAttribute('data-mf-analytics-disabled')) return true;
      return false;
    } catch (_) { return true; }
  }

  function start(options = {}) {
    if (instance) return instance;
    // Explicit start is required. There is no implicit Shopify publisher or pre-consent queue.
    const publish = options.publish;
    const analyticsAllowed = options.analyticsAllowed;
    let stopped = false;
    const removers = [], pending = new Set(), watches = [];
    const delivered = new Set();
    let data = null, outfitRoot = null, workspace = null;
    let items = new Map(), selected = new Map(), observer = null, mutation = null;
    let initAttempted = false;

    function allowed() {
      if (stopped || excluded() || doc.hidden || typeof publish !== 'function' || typeof analyticsAllowed !== 'function') return false;
      try { return analyticsAllowed() === true; } catch (_) { return false; }
    }
    function emit(name, detail, onceKey) {
      if (!EVENTS.has(name) || !allowed() || (onceKey && delivered.has(onceKey))) return false;
      const base = data ? {outfit_handle: slug(data.handle), aesthetic: aesthetic(data.style), page_type: 'outfit'} : {page_type: 'style-guide'};
      const payload = Object.freeze({...base, ...detail});
      try {
        const result = publish(name, payload);
        if (result === false) return false;
        // Async transports are fire-and-forget; rejection must never affect shopping.
        if (result && typeof result.catch === 'function') result.catch(() => {});
        if (onceKey) delivered.add(onceKey);
        return true;
      } catch (_) { return false; }
    }
    function later(fn, delay = 0) {
      const id = win.setTimeout(() => { pending.delete(id); if (!stopped) fn(); }, delay);
      pending.add(id); return id;
    }
    function cancel(id) { if (id !== null) { win.clearTimeout(id); pending.delete(id); } }
    function listen(target, event, fn, capture = false) {
      target.addEventListener(event, fn, capture);
      removers.push(() => target.removeEventListener(event, fn, capture));
    }
    function visible(node, half = false) {
      if (!node || !node.isConnected || node.hidden || doc.hidden) return false;
      const style = win.getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
      const r = node.getBoundingClientRect();
      if (r.width <= 0 || r.height <= 0) return false;
      const width = Math.max(0, Math.min(r.right, win.innerWidth) - Math.max(r.left, 0));
      const height = Math.max(0, Math.min(r.bottom, win.innerHeight) - Math.max(r.top, 0));
      return half ? width * height >= r.width * r.height * 0.5 : width > 0 && height > 0;
    }
    function refreshWatch(watch) {
      if (delivered.has(watch.name)) return;
      const next = watch.getNode();
      if (watch.node !== next) {
        if (watch.node) observer?.unobserve(watch.node);
        cancel(watch.timer); watch.timer = null; watch.node = next;
        if (next) observer?.observe(next);
      }
      if (!allowed() || !visible(next, watch.half)) { cancel(watch.timer); watch.timer = null; return; }
      if (watch.timer !== null) return;
      watch.timer = later(() => {
        watch.timer = null;
        if (allowed() && visible(watch.node, watch.half)) emit(watch.name, {cta_location: watch.location}, watch.name);
      }, watch.duration);
    }
    function recheck() { if (!stopped) { initializeOutfit(); watches.forEach(refreshWatch); } }
    function initializeOutfit() {
      if (initAttempted) return;
      const dataNode = doc.querySelector('[data-outfit-data]');
      outfitRoot = doc.querySelector('[data-outfit-root]');
      workspace = doc.querySelector('.piece-workspace');
      if (!dataNode || !outfitRoot || !workspace || doc.documentElement.dataset.outfitReady !== 'true') return;
      initAttempted = true;
      try {
        const parsed = JSON.parse(dataNode.textContent);
        if (!slug(parsed.handle) || !Array.isArray(parsed.items) || !Array.isArray(parsed.slots)) return;
        data = parsed;
        items = new Map(data.items.filter(i => slug(i.id) && slug(i.slot) && url(i.url)).map(i => [i.id,i]));
        selected = new Map(data.slots.map(s => [s, data.items.find(i => i.slot === s)?.id]));
        // A visitor may have made choices while the tag/consent adapter was loading.
        data.slots.forEach(s => { const current = selectedId(s); if (items.get(current)?.slot === s) selected.set(s, current); });
        watches.push({name:'outfit_view', getNode:() => outfitRoot, duration:300, half:false, location:'outfit-page', node:null, timer:null});
        watches.push({name:'build_look_view', getNode:() => workspace.querySelector('.workspace-heading'), duration:750, half:true, location:'build-the-look', node:null, timer:null});
      } catch (_) { data = null; }
    }
    function closest(target, selector) { return target?.closest ? target.closest(selector) : target?.parentElement?.closest(selector); }
    function itemDetail(item) {
      const destination = url(item.url);
      return {catalog_item_id:slug(item.id), piece_slot:slug(item.slot), retailer:label(item.retailer), destination_host:destination.hostname,
        relationship:affiliate(item) ? 'affiliate' : 'external'};
    }
    function selectedId(slot) {
      const card = Array.from(workspace?.querySelectorAll('[data-slot-card]') || []).find(n => n.dataset.slotCard === slot);
      return card?.querySelector('[data-shop-link]')?.dataset.itemId || selected.get(slot);
    }
    function retailerClick(event) {
      if (event.isTrusted !== true || event.defaultPrevented || !allowed()) return;
      if (event.type === 'auxclick' ? event.button !== 1 : event.button !== 0) return;
      const anchor = closest(event.target, 'a[data-shop-link], a[data-mf-retailer-link]');
      if (!anchor || !visible(anchor)) return;
      let item;
      if (anchor.hasAttribute('data-shop-link') && data) {
        item = items.get(anchor.dataset.itemId);
        if (!item || url(anchor.href)?.href !== url(item.url)?.href) return;
      } else if (anchor.hasAttribute('data-mf-retailer-link')) {
        const d = anchor.dataset, destination = url(anchor.href);
        if (!destination || !slug(d.itemId) || !slug(d.slot) || !label(d.retailer) || !['affiliate','external'].includes(d.relationship)) return;
        item = {id:d.itemId, slot:d.slot, retailer:d.retailer, url:destination.href, kind:d.relationship, commission:d.relationship === 'affiliate'};
      } else return;
      const detail = {...itemDetail(item), cta_location:CTA.has(anchor.dataset.ctaLocation) ? anchor.dataset.ctaLocation : 'comparison',
        input_method:event.type === 'auxclick' ? 'middle' : event.detail === 0 ? 'keyboard' : 'pointer'};
      if (data) detail.selected = selectedId(item.slot) === item.id ? 'yes' : 'no';
      else detail.aesthetic = aesthetic(anchor.dataset.aesthetic);
      // No preventDefault, redirects, callbacks, awaits or navigation delay.
      emit(affiliate(item) ? 'affiliate_click' : 'external_click', detail);
    }
    function click(event) {
      retailerClick(event);
      if (event.isTrusted !== true || event.button !== 0 || event.defaultPrevented || !allowed() || !data) return;
      const opener = closest(event.target, '[data-open-piece]');
      if (!opener || !workspace.contains(opener) || opener.getAttribute('aria-expanded') === 'true') return;
      const slot = opener.dataset.openPiece;
      if (!data.slots.includes(slot)) return;
      const permittedAtAction = allowed();
      later(() => {
        const panel = workspace.querySelector('#card-comparison');
        if (!permittedAtAction || !allowed() || !panel || panel.hidden || opener.getAttribute('aria-expanded') !== 'true') return;
        const item = items.get(selectedId(slot));
        if (!item) return;
        emit('compare_open', {...itemDetail(item), cta_location:'card-inline-opener', opener_kind:opener.dataset.pieceOpener === 'image' ? 'image' : 'details',
          option_count:data.items.filter(i => i.slot === slot).length});
      });
    }
    function change(event) {
      if (event.isTrusted !== true || !data) return;
      const input = closest(event.target, 'input[data-select-item]');
      if (!input?.checked || !workspace.contains(input)) return;
      const item = items.get(input.value);
      if (!item) return;
      const previous = selected.get(item.slot), permittedAtAction = allowed();
      // Selection state is UI state, not an analytics queue. Keep it accurate while opted out.
      selected.set(item.slot, item.id);
      if (previous === item.id) return;
      later(() => {
        if (!permittedAtAction || !allowed() || selectedId(item.slot) !== item.id) return;
        emit('piece_change', {...itemDetail(item), previous_catalog_item_id:slug(previous), cta_location:'card-inline-offer'});
      });
    }

    instance = Object.freeze({version:VERSION, recheck, stop() {
      if (stopped) return;
      stopped = true; pending.forEach(id => win.clearTimeout(id)); pending.clear();
      removers.forEach(remove => remove()); observer?.disconnect(); mutation?.disconnect();
    }});
    if (excluded()) return instance;
    // Capture the before-state; delayed comparison/selection checks run after the renderer.
    listen(doc, 'click', click, true);
    listen(doc, 'auxclick', retailerClick, true);
    listen(doc, 'change', change, true);
    listen(doc, 'visibilitychange', recheck);
    listen(doc, 'visitorConsentCollected', recheck);
    listen(doc, 'mantleford:analytics-preferences-saved', recheck);
    listen(doc, 'DOMContentLoaded', recheck);
    listen(win, 'scroll', recheck, true);
    listen(win, 'resize', recheck);
    if (typeof win.IntersectionObserver === 'function') observer = new win.IntersectionObserver(recheck, {threshold:[0,0.5,1]});
    // Re-rendered card/heading regions need fresh observation, never fresh listeners.
    mutation = typeof win.MutationObserver === 'function' ? new win.MutationObserver(recheck) : null;
    mutation?.observe(doc.documentElement, {subtree:true, childList:true, attributes:true, attributeFilter:['data-outfit-ready']});
    recheck();
    return instance;
  }
  return Object.freeze({version:VERSION, start});
});
